<template>
  <div class="agent-chat" :class="{ expanded: isExpanded, sidebar: isSidebar }">
    <!-- Chat Toggle Button (Only in non-sidebar mode) -->
    <button v-if="!isExpanded && !isSidebar" @click="toggleChat" class="chat-toggle">
      <i class="fas fa-robot"></i>
      <span class="toggle-label">Compliance Agent</span>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </button>

    <!-- Chat Panel -->
    <div v-if="isExpanded || isSidebar" class="chat-panel">
      <div class="chat-header">
        <div class="header-title">
          <i class="fas fa-robot"></i>
          <span>Compliance Agent</span>
        </div>
        <div class="header-actions">
          <button @click="toggleMemoryPanel" class="btn-icon" :class="{ active: showMemoryPanel }" title="Agent Memory">
            <i class="fas fa-brain"></i>
          </button>
          <button @click="clearChat" class="btn-icon" title="Clear chat">
            <i class="fas fa-trash-alt"></i>
          </button>
          <button v-if="!isSidebar" @click="toggleChat" class="btn-icon" title="Minimize">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>
      
      <!-- Agent Memory Panel -->
      <div v-if="showMemoryPanel" class="memory-panel">
        <div class="memory-header">
          <h5><i class="fas fa-brain"></i> Agent Learning</h5>
          <button @click="refreshMemories" class="btn-icon-sm" title="Refresh">
            <i class="fas fa-sync" :class="{ 'fa-spin': loadingMemories }"></i>
          </button>
        </div>
        <div v-if="loadingMemories" class="memory-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading memories...
        </div>
        <div v-else-if="memories.length === 0" class="memory-empty">
          <i class="fas fa-lightbulb"></i>
          <p>No memories yet. The agent learns from your feedback!</p>
        </div>
        <div v-else class="memory-list">
          <div v-for="mem in memories" :key="mem.id" class="memory-item" :class="mem.type">
            <div class="memory-icon">
              <i :class="getMemoryIcon(mem.type)"></i>
            </div>
            <div class="memory-content">
              <div class="memory-text">{{ mem.content }}</div>
              <div class="memory-meta">
                <span class="memory-type">{{ mem.type }}</span>
                <span class="memory-date">{{ formatMemoryDate(mem.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h4>Compliance Agent</h4>
          <p>I can help you manage compliance across your portfolio. Try asking:</p>
          <div class="suggestions">
            <button @click="sendSuggestion('What are the top issues I should address?')" class="suggestion-btn">
              What are the top issues?
            </button>
            <button @click="sendSuggestion('Show me entities with expired policies')" class="suggestion-btn">
              Expired policies
            </button>
            <button @click="triggerRemediationUpload" class="suggestion-btn" :disabled="!projectId">
              Upload endorsement / COI
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message"
          :class="message.role"
        >
          <div class="message-avatar">
            <i :class="message.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
          </div>
          <div class="message-content">
            <div class="message-text" v-html="formatMessage(message.content)"></div>
            
            <!-- Action Buttons if present -->
            <div v-if="message.actions?.length" class="message-actions">
              <button 
                v-for="(action, actionIndex) in message.actions" 
                :key="actionIndex"
                @click="executeAction(action)"
                class="action-btn"
                :class="action.type"
                :disabled="action.executed"
                :title="action.type === 'notify' ? 'Opens draft preview - you can review and edit before sending' : ''"
              >
                <i :class="action.icon || 'fas fa-play'"></i>
                {{ action.label }}
                <span v-if="action.type === 'notify' && !action.executed" class="draft-hint">(click to preview)</span>
                <span v-if="action.executed" class="executed-badge">Done</span>
              </button>
            </div>

            <!-- Entity List if present -->
            <div v-if="message.entityList?.length" class="entity-preview">
              <div class="entity-preview-header">
                <span>{{ message.entityList.length }} entities</span>
                <button @click="viewFullList(message.entityList)" class="view-all-btn">
                  View All
                </button>
              </div>
              <div class="entity-preview-list">
                <div v-for="entity in message.entityList.slice(0, 5)" :key="entity.id" class="preview-entity">
                  <span class="entity-name">{{ entity.name }}</span>
                  <span class="entity-issue">{{ entity.issue }}</span>
                </div>
                <div v-if="message.entityList.length > 5" class="more-entities">
                  +{{ message.entityList.length - 5 }} more
                </div>
              </div>
            </div>

            <!-- Documents List if present -->
            <div v-if="message.documents?.length" class="documents-list">
              <div class="documents-header">
                <i class="fas fa-file-alt"></i>
                <span>{{ message.documents.length }} document{{ message.documents.length > 1 ? 's' : '' }}</span>
              </div>
              <div class="documents-grid">
                <div 
                  v-for="(doc, docIndex) in message.documents" 
                  :key="docIndex"
                  class="document-card"
                  @click="openDocument(doc)"
                >
                  <div class="doc-icon">
                    <i class="fas fa-file-pdf"></i>
                  </div>
                  <div class="doc-info">
                    <div class="doc-name">{{ doc.filename || doc.name || doc.file_name || 'Document' }}</div>
                    <div class="doc-meta">
                      <span class="doc-type">{{ doc.document_type || 'UNKNOWN' }}</span>
                      <span class="doc-date">{{ formatDocDate(doc.uploaded_at || doc.created_at) }}</span>
                    </div>
                  </div>
                  <div class="doc-action">
                    <i class="fas fa-external-link-alt"></i>
                  </div>
                </div>
              </div>
            </div>

            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            
            <!-- Feedback Buttons for Assistant Messages with Analysis -->
            <div v-if="message.role === 'assistant' && message.hasAnalysis" class="message-feedback">
              <button 
                v-if="!message.feedbackGiven"
                @click="submitFeedback(message, 'approval')" 
                class="feedback-btn approve"
                title="This analysis is correct"
              >
                <i class="fas fa-thumbs-up"></i>
                Helpful
              </button>
              <button 
                v-if="!message.feedbackGiven"
                @click="openCorrectionModal(message)" 
                class="feedback-btn correct"
                title="Correct this analysis"
              >
                <i class="fas fa-edit"></i>
                Correct
              </button>
              <span v-if="message.feedbackGiven" class="feedback-thanks">
                <i class="fas fa-check-circle"></i>
                Thanks for your feedback!
              </span>
            </div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="message assistant typing">
          <div class="message-avatar">
            <i class="fas fa-robot"></i>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="chat-input">
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,application/pdf"
          class="hidden-file-input"
          @change="onRemediationFileSelected"
        />
        <button
          type="button"
          class="attach-btn"
          title="Upload endorsement / COI / policy for remediation"
          :disabled="isTyping || remediationUploading || !projectId"
          @click="triggerRemediationUpload"
        >
          <i :class="remediationUploading ? 'fas fa-spinner fa-spin' : 'fas fa-paperclip'"></i>
        </button>
        <textarea 
          v-model="inputMessage"
          @keydown.enter.prevent="sendMessage"
          placeholder="Ask about compliance, or attach a PDF to remediate..."
          rows="1"
          ref="inputField"
        ></textarea>
        <button @click="sendMessage" :disabled="!inputMessage.trim() || isTyping" class="send-btn">
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
    
    <!-- Correction Modal -->
    <div v-if="showCorrectionModal" class="modal-overlay" @click.self="closeCorrectionModal">
      <div class="modal-content correction-modal">
        <div class="modal-header">
          <h4><i class="fas fa-edit"></i> Correct Analysis</h4>
          <button @click="closeCorrectionModal" class="btn-icon">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="correction-intro">Help improve the agent by correcting its analysis.</p>
          
          <div class="form-group">
            <label>Original Analysis</label>
            <div class="original-analysis">{{ correctionData.original }}</div>
          </div>
          
          <div class="form-group">
            <label for="corrected">What should it have been?</label>
            <textarea 
              id="corrected"
              v-model="correctionData.corrected"
              class="form-control"
              rows="3"
              placeholder="e.g., The entity has a GL policy but the limits are below requirements"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="reason">Why is this the correct analysis? (optional)</label>
            <textarea 
              id="reason"
              v-model="correctionData.reason"
              class="form-control"
              rows="2"
              placeholder="e.g., I verified the policy exists in our records"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCorrectionModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="submitCorrection" 
            class="btn btn-primary"
            :disabled="!correctionData.corrected || submittingFeedback"
          >
            <i v-if="submittingFeedback" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            Submit Correction
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, watch, onMounted } from 'vue'
import api from '@/services/api'
import { sanitizeLlmResponse } from '@/utils/sanitize'

export default {
  name: 'ComplianceAgentChat',
  
  props: {
    projectId: {
      type: [String, Number],
      default: null
    },
    summary: {
      type: Object,
      default: () => ({})
    },
    isSidebar: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['filter-entities', 'select-entities', 'notify', 'show-results', 'refresh-data', 'create-entity', 'show-entity-details'],
  
  setup(props, { emit }) {
    const isExpanded = ref(false)
    const messages = ref([])
    const inputMessage = ref('')
    const isTyping = ref(false)
    const unreadCount = ref(0)
    const messagesContainer = ref(null)
    const inputField = ref(null)
    const fileInput = ref(null)
    const remediationUploading = ref(false)
    const pendingRemediation = ref(null)
    
    // Memory and feedback state
    const showMemoryPanel = ref(false)
    const memories = ref([])
    const loadingMemories = ref(false)
    const showCorrectionModal = ref(false)
    const submittingFeedback = ref(false)
    const correctionData = ref({
      messageIndex: null,
      original: '',
      corrected: '',
      reason: '',
      snapshotId: null
    })
    
    const openChat = () => {
      if (props.isSidebar) return
      isExpanded.value = true
      unreadCount.value = 0
      nextTick(() => {
        inputField.value?.focus()
      })
    }

    const toggleChat = () => {
      if (props.isSidebar) return // Can't toggle in sidebar mode
      isExpanded.value = !isExpanded.value
      if (isExpanded.value) {
        unreadCount.value = 0
        nextTick(() => {
          inputField.value?.focus()
        })
      }
    }

    onMounted(() => {
      if (props.isSidebar) {
        isExpanded.value = true
        nextTick(() => {
          scrollToBottom()
        })
      }
    })
    
    const clearChat = () => {
      messages.value = []
    }
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const formatMessage = (content) => {
      // Use DOMPurify-based sanitization for XSS protection
      return sanitizeLlmResponse(content)
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    
    const formatDocDate = (timestamp) => {
      if (!timestamp) return 'Unknown date'
      const date = new Date(timestamp)
      return date.toLocaleDateString()
    }
    
    const openDocument = (doc) => {
      // Get the document URL - try multiple possible field names
      const url = doc.file_url || doc.fileUrl || doc.url || doc.file
      
      if (url) {
        window.open(url, '_blank')
      } else {
        console.error('No URL found for document:', doc)
        if (window.$toast) {
          window.$toast.error('Document URL not available')
        }
      }
    }
    
    const sendSuggestion = (text) => {
      inputMessage.value = text
      sendMessage()
    }
    
    const sendMessage = async () => {
      const text = inputMessage.value.trim()
      if (!text || isTyping.value) return
      
      // Add user message
      messages.value.push({
        role: 'user',
        content: text,
        timestamp: new Date().toISOString()
      })
      
      inputMessage.value = ''
      scrollToBottom()
      
      // Show typing indicator
      isTyping.value = true
      
      try {
        // Call the agent API
        const response = await api.post('/compliance-health/agent/chat/', {
          message: text,
          project_id: props.projectId,
          context: {
            summary: props.summary,
            history: messages.value.slice(-10).map(m => ({
              role: m.role,
              content: m.content
            }))
          }
        })
        
        // Add assistant response
        // Mark as having analysis if it contains entity results or coverage data
        const hasAnalysisContent = !!(
          response.data.entities?.length > 0 ||
          response.data.coverage_query_results?.entities?.length > 0 ||
          response.data.root_causes?.length > 0 ||
          response.data.analysis_type
        )
        
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString(),
          actions: response.data.actions || [],
          entityList: response.data.entities || [],
          coverageQueryResults: response.data.coverage_query_results || null,
          hasAnalysis: hasAnalysisContent,
          snapshotId: response.data.snapshot_id || null,
          feedbackGiven: false
        }
        
        messages.value.push(assistantMessage)
        
        // If there are coverage query results with entities, auto-show in results panel
        if (response.data.coverage_query_results?.entities?.length > 0) {
          console.log('Coverage query results entities:', response.data.coverage_query_results.entities)
          const coverageEntities = response.data.coverage_query_results.entities.map(e => ({
            entity_id: e.entity_id || e.id,
            subject_id: e.subject_id || e.entity_id || e.id,
            entity_name: e.entity_name || e.name || '',
            name: e.name || e.entity_name || '',
            status: e.status || e.overall_status || 'Unknown',
            overall_status: e.overall_status || e.status || 'Unknown',
            score: e.score || e.overall_score,
            trend: e.trend || null,
            coverage_summary: e.coverage_summary || null,
            failing_requirements: e.failing_requirements || [],
            total_requirements: e.total_requirements || null,
            passing_requirements: e.passing_requirements || null
          }))
          emit('show-results', {
            title: `${response.data.coverage_query_results.coverage_name || 'Coverage'} Query`,
            subtitle: `Found ${coverageEntities.length} entities`,
            coverageType: response.data.coverage_query_results.coverage_name,
            queryConfidence: response.data.coverage_query_results.query_confidence,
            entities: coverageEntities,
            message: response.data.message
          })
        } else if (response.data.entities?.length > 0) {
          // Also auto-show if entities were returned directly
          console.log('Raw entities from response:', JSON.stringify(response.data.entities, null, 2))
          const entityCount = response.data.entities.length
          const resultTitle = entityCount === 1
            ? (response.data.entities[0].entity_name || response.data.entities[0].name || 'Entity Details')
            : 'Query Results'
          const resultSubtitle = entityCount === 1
            ? 'Compliance overview'
            : `Found ${entityCount} entities`
          emit('show-results', {
            title: resultTitle,
            subtitle: resultSubtitle,
            entities: response.data.entities.map(e => ({
              entity_id: e.entity_id || e.id,
              subject_id: e.subject_id || e.entity_id || e.id,
              entity_name: e.entity_name || e.name || '',
              name: e.name || e.entity_name || '',
              status: e.status || e.overall_status || 'Unknown',
              overall_status: e.overall_status || e.status || 'Unknown',
              score: e.score || e.overall_score,
              trend: e.trend || null,
              coverage_summary: e.coverage_summary || null,
              failing_requirements: e.failing_requirements || (e.issue ? [{ name: e.issue }] : []),
              total_requirements: e.total_requirements || null,
              passing_requirements: e.passing_requirements || null
            })),
            message: response.data.message
          })
        }
        
        // Increment unread if chat is minimized
        if (!isExpanded.value) {
          unreadCount.value++
        }
        
      } catch (error) {
        console.error('Error sending message:', error)
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString()
        })
      } finally {
        isTyping.value = false
        scrollToBottom()
      }
    }
    
    const executeAction = async (action) => {
      if (action.executed) return
      
      // Normalize entity IDs - LLM might use different keys
      const entityIds = action.entityIds || action.entity_ids || action.ids || []
      
      console.log('Executing action:', action)
      console.log('Entity IDs:', entityIds)
      
      if (action.type === 'notify') {
        // Validate entity IDs
        if (!entityIds || entityIds.length === 0) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ No entities were specified for this action. Please ask me about specific entities first.',
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
          return
        }
        
        // Emit to parent to show preview in center pane
        emit('notify', { ...action, entityIds })
        
      } else if (action.type === 'report') {
        // Validate entity IDs
        if (!entityIds || entityIds.length === 0) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ No entities were specified for the report. Please ask me about specific entities first.',
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
          return
        }
        
        try {
          // Generate and download report
          const response = await api.post('/compliance-health/agent/report/', {
            entity_ids: entityIds,
            report_type: action.reportType,
            project_id: props.projectId
          }, {
            responseType: 'blob'
          })
          
          // Download the file
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', action.filename || 'compliance-report.xlsx')
          document.body.appendChild(link)
          link.click()
          link.remove()
          
          action.executed = true
        } catch (error) {
          console.error('Error generating report:', error)
          messages.value.push({
            role: 'assistant',
            content: `❌ Failed to generate report: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
        }
        
      } else if (action.type === 'select') {
        // Select entities in the dashboard
        emit('select-entities', entityIds)
        action.executed = true
        
      } else if (action.type === 'create_entity') {
        // Emit to parent to show entity creation modal
        emit('create-entity', {
          entity_name: action.entity_name || action.entityName || '',
          entity_type: action.entity_type || action.entityType || 'COMPANY',
          email: action.email || '',
          phone: action.phone || '',
          address: action.address || {}
        })
        action.executed = true
        
      } else if (action.type === 'use_existing_entity') {
        // Use an existing entity to create a ComplianceSubject
        const entityId = action.entity_id || action.entityId
        const entityName = action.entity_name || action.entityName || 'Unknown'
        
        if (!entityId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No entity ID provided for existing entity.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        if (!props.projectId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: Please select a project first before adding entities.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.post('/compliance-health/agent/use-existing-entity/', {
            entity_id: entityId,
            project_id: props.projectId
          })
          
          if (response.data.success) {
            action.executed = true
            let statusMsg = `✅ Added "${entityName}" to compliance tracking. ${response.data.already_existed ? '(Already existed in project)' : '(New compliance subject created)'}`

            if (response.data.coi_request_sent === 'scheduled') {
              statusMsg += `\n📧 COI request email will be sent to ${response.data.coi_email_to}`
            } else if (response.data.coi_email_reason === 'no_contact_email') {
              statusMsg += '\n⚠️ No contact email found on entity or agent — COI request email was not sent.'
            } else if (response.data.coi_email_reason === 'has_document') {
              statusMsg += '\n📄 Document already on file — no COI request needed.'
            }

            messages.value.push({
              role: 'assistant',
              content: statusMsg,
              timestamp: new Date().toISOString()
            })
            emit('refresh-data')
          } else {
            messages.value.push({
              role: 'assistant',
              content: `⚠️ Could not add entity: ${response.data.error || 'Unknown error'}`,
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error using existing entity:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error adding entity: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
        }
        
      } else if (action.type === 'export_no_data') {
        // Download entities without compliance data
        try {
          const format = action.format || 'xlsx'
          const params = new URLSearchParams()
          params.append('format', format)
          if (props.projectId) {
            params.append('project_id', props.projectId)
          }
          
          const response = await api.get(`/compliance-health/export-no-data/?${params.toString()}`, {
            responseType: 'blob'
          })
          
          // Download the file
          const blob = new Blob([response.data], {
            type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `no_data_entities_${new Date().toISOString().split('T')[0]}.${format}`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          
          action.executed = true
          messages.value.push({
            role: 'assistant',
            content: `✅ Downloaded list of entities without compliance data (${format.toUpperCase()} format).`,
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
        } catch (error) {
          console.error('Export error:', error)
          messages.value.push({
            role: 'assistant',
            content: `❌ Failed to export: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
        }

      } else if (action.type === 'refresh_health_summaries') {
        action.loading = true
        try {
          const payload = {
            project_id: props.projectId,
            subject_ids: action.entityIds || action.subject_ids || action.payload?.subject_ids || [],
            force: !!(action.force || action.payload?.force),
            include_llm_summary: !!(
              action.include_llm_summary || action.payload?.include_llm_summary
            ),
            dry_run: false,
          }
          if (!payload.subject_ids.length) {
            delete payload.subject_ids
          }
          const response = await api.post(
            '/compliance-health/agent/refresh-health-summaries/',
            payload
          )
          action.executed = true
          const data = response.data || {}
          messages.value.push({
            role: 'assistant',
            content: data.message
              || `Queued health refresh for ${data.queued || 0} entities.`,
            timestamp: new Date().toISOString()
          })
          emit('refresh-data')
          scrollToBottom()
        } catch (error) {
          console.error('Health refresh error:', error)
          messages.value.push({
            role: 'assistant',
            content: `❌ Failed to refresh health summaries: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
          scrollToBottom()
        } finally {
          action.loading = false
        }
        
      } else if (action.type === 'show_documents') {
        // Show documents for an entity
        const entityId = action.entity_id || action.entityId
        const entityName = action.entity_name || action.entityName || 'Entity'
        
        if (!entityId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No entity ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.get(`/compliance-health/agent/entity/${entityId}/documents/`)
          
          if (response.data.success && response.data.documents.length > 0) {
            let docList = response.data.documents.map(d => 
              `📄 ${d.filename} (${d.document_type}) - ${d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : 'Unknown date'}`
            ).join('\n')
            
            messages.value.push({
              role: 'assistant',
              content: `📄 **Documents for ${entityName}** (${response.data.total} total):\n\n${docList}`,
              timestamp: new Date().toISOString(),
              documents: response.data.documents
            })
            action.executed = true
          } else {
            messages.value.push({
              role: 'assistant',
              content: `📄 No documents found for ${entityName}.`,
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error fetching documents:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error fetching documents: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
          scrollToBottom()
        }
        
      } else if (action.type === 'view_document') {
        // Open a specific document
        const docId = action.document_id || action.documentId
        const docName = action.document_name || action.documentName || 'Document'
        
        if (docId) {
          // Open document in new tab or emit to parent
          window.open(`/documents/${docId}`, '_blank')
          action.executed = true
          messages.value.push({
            role: 'assistant',
            content: `📄 Opened ${docName} in a new tab.`,
            timestamp: new Date().toISOString()
          })
        }
        
      } else if (action.type === 'show_notification_history') {
        // Show notification/email history for an entity
        const entityId = action.entity_id || action.entityId
        const entityName = action.entity_name || action.entityName || 'Entity'
        
        if (!entityId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No entity ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.get(`/compliance-health/agent/entity/${entityId}/notifications/`)
          
          if (response.data.success && response.data.notifications.length > 0) {
            let notifList = response.data.notifications.slice(0, 10).map(n => {
              const statusIcon = n.status === 'OPENED' || n.status === 'CLICKED' ? '✅' : 
                                 n.status === 'DELIVERED' ? '📧' : 
                                 n.status === 'SENT' ? '📤' : '⚠️'
              const openedInfo = n.opened_at ? ` (Opened: ${new Date(n.opened_at).toLocaleDateString()})` : ''
              return `${statusIcon} ${new Date(n.sent_at).toLocaleDateString()} - "${n.subject}" - ${n.status}${openedInfo}`
            }).join('\n')
            
            messages.value.push({
              role: 'assistant',
              content: `📧 **Notification History for ${entityName}** (${response.data.total} total):\n\n${notifList}`,
              timestamp: new Date().toISOString(),
              notifications: response.data.notifications
            })
            action.executed = true
          } else {
            messages.value.push({
              role: 'assistant',
              content: `📧 No notification history found for ${entityName}. ${!response.data.entity_email ? '(No email address on file)' : ''}`,
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error fetching notification history:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error fetching notification history: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
          scrollToBottom()
        }
        
      } else if (action.type === 'show_activity') {
        // Show activity timeline for a subject
        const subjectId = action.subject_id || action.subjectId
        const entityName = action.entity_name || action.entityName || 'Entity'
        
        if (!subjectId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No subject ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.get(`/compliance-health/agent/subject/${subjectId}/activity/`)
          
          if (response.data.success && response.data.activities.length > 0) {
            let activityList = response.data.activities.slice(0, 10).map(a => {
              const icon = a.type === 'action' ? (a.status === 'SUCCESS' ? '✅' : '⚠️') : '🤖'
              const date = a.timestamp ? new Date(a.timestamp).toLocaleDateString() : 'Unknown'
              const desc = a.type === 'action' ? a.action_name : a.log_type
              return `${icon} ${date} - ${desc}${a.status ? ` (${a.status})` : ''}`
            }).join('\n')
            
            messages.value.push({
              role: 'assistant',
              content: `📋 **Activity Timeline for ${entityName}** (${response.data.total} events):\n\n${activityList}`,
              timestamp: new Date().toISOString(),
              activities: response.data.activities
            })
            action.executed = true
          } else {
            messages.value.push({
              role: 'assistant',
              content: `📋 No activity history found for ${entityName}.`,
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error fetching activity history:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error fetching activity history: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
          scrollToBottom()
        }
        
      } else if (action.type === 'show_entity_details') {
        // Show entity detail panel in the dashboard
        const subjectId = action.subject_id || action.subjectId
        const entityName = action.entity_name || action.entityName || 'Entity'
        
        if (!subjectId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No subject ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        // Emit to parent to select and show entity details
        emit('show-entity-details', subjectId)
        action.executed = true
        
        messages.value.push({
          role: 'assistant',
          content: `✅ Showing details for ${entityName} in the detail panel.`,
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
        
      } else if (action.type === 'show_score_trend') {
        // Show score trend for a subject
        const subjectId = action.subject_id || action.subjectId
        const entityName = action.entity_name || action.entityName || 'Entity'
        
        if (!subjectId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No subject ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.get(`/compliance-health/agent/subject/${subjectId}/score-trend/`)
          
          if (response.data.success && response.data.history.length > 0) {
            const trendIcon = response.data.trend_direction === 'improving' ? '📈' : 
                             response.data.trend_direction === 'declining' ? '📉' : '➡️'
            
            let trendList = response.data.history.slice(0, 10).map(h => {
              const date = h.calculated_at ? new Date(h.calculated_at).toLocaleDateString() : 'Unknown'
              return `${h.score}% (${h.status}) - ${date}`
            }).join('\n')
            
            messages.value.push({
              role: 'assistant',
              content: `${trendIcon} **Score Trend for ${entityName}**\n\nCurrent: ${response.data.current_score}% | Trend: ${response.data.trend_direction}\n\n${trendList}`,
              timestamp: new Date().toISOString(),
              scoreTrend: response.data
            })
            action.executed = true
          } else {
            messages.value.push({
              role: 'assistant',
              content: `📈 No score history available for ${entityName}.`,
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error fetching score trend:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error fetching score trend: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
          scrollToBottom()
        }
        
      } else if (action.type === 'request_documents') {
        // Request documents from an entity (generate upload link)
        const entityId = action.entity_id || action.entityId
        const entityName = action.entity_name || action.entityName || 'Entity'
        const sendEmail = action.send_email !== false
        
        if (!entityId) {
          messages.value.push({
            role: 'assistant',
            content: '⚠️ Error: No entity ID provided.',
            timestamp: new Date().toISOString()
          })
          return
        }
        
        try {
          action.loading = true
          const response = await api.post('/compliance-health/agent/request-documents/', {
            entity_id: entityId,
            project_id: props.projectId,
            send_email: sendEmail
          })
          
          if (response.data.success) {
            let message = `📤 **Document Request Created for ${entityName}**\n\n`
            message += `🔗 Upload Link: ${response.data.upload_url}\n`
            message += `⏰ Expires: ${response.data.expires_at ? new Date(response.data.expires_at).toLocaleDateString() : 'Unknown'}\n`
            
            // Continue existing success handling below — do not break structure
            action.executed = true
            messages.value.push({
              role: 'assistant',
              content: message + (response.data.email_sent
                ? `\n✅ Email sent to agent.`
                : `\n📧 Email not sent — share the link manually.`),
              timestamp: new Date().toISOString()
            })
          }
        } catch (error) {
          console.error('Error requesting documents:', error)
          messages.value.push({
            role: 'assistant',
            content: `⚠️ Error requesting documents: ${error.response?.data?.error || error.message}`,
            timestamp: new Date().toISOString()
          })
        } finally {
          action.loading = false
          scrollToBottom()
        }

      } else if (
        action.type === 'confirm_document_type'
        || action.type === 'confirm_subject'
        || action.type === 'send_clearance'
        || action.type === 'send_remaining_gaps'
        || action.type === 'skip'
        || action.type === 'refresh_posture'
      ) {
        await handleRemediationAction(action)
        return
      }
    }
    
    const triggerRemediationUpload = () => {
      if (!props.projectId) {
        messages.value.push({
          role: 'assistant',
          content: 'Select a project first, then attach the PDF the agent emailed you.',
          timestamp: new Date().toISOString()
        })
        return
      }
      fileInput.value?.click()
    }

    const onRemediationFileSelected = async (event) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      await uploadRemediationFile(file)
    }

    const formatRemediationResult = (data) => {
      const lines = []
      lines.push(`📎 **Uploaded:** ${data.document_name || 'document'} (${data.page_count || '?'} pages)`)
      lines.push(`Type: ${data.classified_document_type || 'unknown'} · Coverages: ${(data.coverage_types || []).join(', ') || 'n/a'}`)

      if (data.needs_type_confirm) {
        const msg = data.classification_metadata?.type_confirm_message
          || data.classification_metadata?.page_prior?.message
          || 'Confirm whether this is a certificate, endorsement, or policy.'
        lines.push(`\n⚠️ ${msg}`)
      }

      const candidates = data.match?.candidates || []
      if (!data.compliance_subject_id && candidates.length) {
        lines.push('\n**Possible matches — pick one:**')
        candidates.slice(0, 5).forEach((c) => {
          lines.push(`- ${c.subject_name || c.entity_name} (confidence ${(c.confidence * 100).toFixed(0)}%)`)
        })
      } else if (data.compliance_subject_id) {
        lines.push(`\nLinked to subject #${data.compliance_subject_id}`)
      } else {
        lines.push('\nNo subject match yet — confirm subject after picking type, or search in chat.')
      }

      const posture = data.posture
      if (posture) {
        lines.push(
          `\n**Subject posture:** ${posture.open_fix_count} open FIX gaps`
          + (posture.is_clear_for_clearance ? ' — clear for clearance email' : '')
        )
      }

      if (data.check_run_id) {
        lines.push(`Check run started: ${data.check_run_id} (${data.check_run_status})`)
        lines.push('When the check finishes, click Refresh posture or send gap/clearance email.')
      }

      return lines.join('\n')
    }

    const buildRemediationActions = (data) => {
      const actions = []
      if (data.needs_type_confirm) {
        ['CERTIFICATE', 'ENDORSEMENT', 'INSURANCE_POLICY'].forEach((t) => {
          actions.push({
            type: 'confirm_document_type',
            label: `Type: ${t}`,
            document_type: t,
            document_id: data.document_id,
            compliance_subject_id: data.compliance_subject_id
          })
        })
      }
      const candidates = data.match?.candidates || []
      if (!data.compliance_subject_id) {
        candidates.slice(0, 5).forEach((c) => {
          actions.push({
            type: 'confirm_subject',
            label: `Use ${c.subject_name || c.entity_name}`,
            compliance_subject_id: c.subject_id,
            document_id: data.document_id
          })
        })
      }
      if (data.compliance_subject_id && data.check_run_id) {
        actions.push({
          type: 'refresh_posture',
          label: 'Refresh posture',
          compliance_subject_id: data.compliance_subject_id,
          document_id: data.document_id
        })
      }
      (data.email_actions || []).forEach((a) => {
        if (a.type === 'blocked_type_confirm') return
        actions.push({
          ...a,
          compliance_subject_id: data.compliance_subject_id,
          document_id: data.document_id,
          check_run_id: a.check_run_id || data.check_run_id
        })
      })
      return actions
    }

    const uploadRemediationFile = async (file) => {
      if (!props.projectId) return
      remediationUploading.value = true
      messages.value.push({
        role: 'user',
        content: `Uploading remediation PDF: ${file.name}`,
        timestamp: new Date().toISOString()
      })
      try {
        const form = new FormData()
        form.append('file', file)
        form.append('project_id', props.projectId)
        const response = await api.post(
          '/compliance-health/agent/remediation-upload/',
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        const data = response.data
        pendingRemediation.value = data
        messages.value.push({
          role: 'assistant',
          content: formatRemediationResult(data),
          timestamp: new Date().toISOString(),
          actions: buildRemediationActions(data)
        })
      } catch (error) {
        console.error('Remediation upload failed:', error)
        messages.value.push({
          role: 'assistant',
          content: `⚠️ Upload failed: ${error.response?.data?.error || error.message}`,
          timestamp: new Date().toISOString()
        })
      } finally {
        remediationUploading.value = false
        scrollToBottom()
      }
    }

    const handleRemediationAction = async (action) => {
      if (!props.projectId) return
      action.loading = true
      try {
        if (action.type === 'confirm_document_type' || action.type === 'confirm_subject') {
          const subjectId = action.compliance_subject_id
            || pendingRemediation.value?.compliance_subject_id
          const documentId = action.document_id || pendingRemediation.value?.document_id
          if (!subjectId || !documentId) {
            messages.value.push({
              role: 'assistant',
              content: 'Need both a subject and document before running the check. Pick a subject match first.',
              timestamp: new Date().toISOString()
            })
            return
          }
          const body = {
            project_id: props.projectId,
            document_id: documentId,
            compliance_subject_id: subjectId
          }
          if (action.document_type) {
            body.confirmed_document_type = action.document_type
          }
          const response = await api.post(
            '/compliance-health/agent/remediation-confirm/',
            body
          )
          const data = response.data
          pendingRemediation.value = { ...pendingRemediation.value, ...data }
          action.executed = true
          messages.value.push({
            role: 'assistant',
            content: formatRemediationResult({
              ...pendingRemediation.value,
              ...data,
              needs_type_confirm: false
            }),
            timestamp: new Date().toISOString(),
            actions: buildRemediationActions({
              ...pendingRemediation.value,
              ...data,
              needs_type_confirm: false,
              email_actions: data.email_actions
            })
          })
        } else if (action.type === 'refresh_posture') {
          const response = await api.get('/compliance-health/agent/remediation-posture/', {
            params: {
              project_id: props.projectId,
              compliance_subject_id: action.compliance_subject_id,
              document_id: action.document_id
            }
          })
          const data = {
            ...pendingRemediation.value,
            ...response.data,
            document_id: action.document_id,
            compliance_subject_id: action.compliance_subject_id
          }
          pendingRemediation.value = data
          action.executed = true
          messages.value.push({
            role: 'assistant',
            content: formatRemediationResult(data),
            timestamp: new Date().toISOString(),
            actions: buildRemediationActions(data)
          })
        } else if (action.type === 'skip') {
          action.executed = true
          messages.value.push({
            role: 'assistant',
            content: 'Skipped agent email. Document is on file.',
            timestamp: new Date().toISOString()
          })
        } else if (action.type === 'send_clearance' || action.type === 'send_remaining_gaps') {
          const response = await api.post(
            '/compliance-health/agent/remediation-send-email/',
            {
              project_id: props.projectId,
              compliance_subject_id: action.compliance_subject_id
                || pendingRemediation.value?.compliance_subject_id,
              action_type: action.type,
              check_run_id: action.check_run_id,
              agent_link_id: action.agent_link_id,
              coverage_types: action.coverage_types
            }
          )
          action.executed = true
          const sent = response.data.sent || (response.data.results || []).some(r => r.sent)
          messages.value.push({
            role: 'assistant',
            content: sent
              ? `✅ Email sent${response.data.email ? ` to ${response.data.email}` : ''}.`
              : `Email not sent: ${response.data.reason || 'see server log'}.`,
            timestamp: new Date().toISOString()
          })
        }
      } catch (error) {
        console.error('Remediation action failed:', error)
        messages.value.push({
          role: 'assistant',
          content: `⚠️ ${error.response?.data?.error || error.response?.data?.message || error.message}`,
          timestamp: new Date().toISOString()
        })
      } finally {
        action.loading = false
        scrollToBottom()
      }
    }

    const viewFullList = (entityList, messageContext = null) => {
      console.log('viewFullList called with:', entityList)
      // Emit event to show full list in results panel with all enhanced fields
      emit('show-results', {
        title: messageContext?.title || 'Query Results',
        subtitle: `Found ${entityList.length} entities`,
        coverageType: messageContext?.coverageType,
        queryConfidence: messageContext?.confidence,
        entities: entityList.map(e => ({
          entity_id: e.entity_id || e.id,
          subject_id: e.subject_id || e.entity_id || e.id,
          entity_name: e.entity_name || e.name || '',
          name: e.name || e.entity_name || '',
          status: e.status || e.overall_status || 'Unknown',
          overall_status: e.overall_status || e.status || 'Unknown',
          score: e.score || e.overall_score,
          trend: e.trend || null,
          coverage_summary: e.coverage_summary || null,
          failing_requirements: e.failing_requirements || (e.issue ? [{ name: e.issue }] : []),
          total_requirements: e.total_requirements || null,
          passing_requirements: e.passing_requirements || null
        })),
        message: messageContext?.message
      })
    }
    
    // =======================================================================
    // MEMORY & FEEDBACK FUNCTIONS
    // =======================================================================
    
    const toggleMemoryPanel = () => {
      showMemoryPanel.value = !showMemoryPanel.value
      if (showMemoryPanel.value && memories.value.length === 0) {
        refreshMemories()
      }
    }
    
    const refreshMemories = async () => {
      loadingMemories.value = true
      try {
        const params = props.projectId 
          ? { project_id: props.projectId, limit: 10 }
          : { limit: 10 }
        
        // Need at least one of subject_id or project_id
        if (!props.projectId) {
          memories.value = []
          loadingMemories.value = false
          return
        }
        
        const response = await api.get('/compliance-health/agent-memory/', { params })
        memories.value = response.data.memories || []
      } catch (error) {
        console.error('Error fetching memories:', error)
        memories.value = []
      } finally {
        loadingMemories.value = false
      }
    }
    
    const getMemoryIcon = (memType) => {
      const icons = {
        episodic: 'fas fa-history',
        semantic: 'fas fa-book',
        procedural: 'fas fa-cogs',
        belief: 'fas fa-lightbulb'
      }
      return icons[memType] || 'fas fa-brain'
    }
    
    const formatMemoryDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      return date.toLocaleDateString()
    }
    
    const openCorrectionModal = (message) => {
      // Extract the main analysis from the message
      const analysisText = extractAnalysisFromMessage(message.content)
      correctionData.value = {
        messageIndex: messages.value.indexOf(message),
        original: analysisText,
        corrected: '',
        reason: '',
        snapshotId: message.snapshotId || null
      }
      showCorrectionModal.value = true
    }
    
    const closeCorrectionModal = () => {
      showCorrectionModal.value = false
      correctionData.value = {
        messageIndex: null,
        original: '',
        corrected: '',
        reason: '',
        snapshotId: null
      }
    }
    
    const extractAnalysisFromMessage = (content) => {
      // Try to extract key analysis points from the message
      // Look for patterns like "Issue:", "Problem:", "Root cause:", etc.
      const patterns = [
        /(?:issue|problem|root cause|finding)[:\s]+([^.]+)/i,
        /(?:missing|expired|under[- ]limit)[^.]+/i,
        /(?:coverage|policy)[^.]+(?:missing|expired|insufficient)[^.]*/i
      ]
      
      for (const pattern of patterns) {
        const match = content.match(pattern)
        if (match) {
          return match[0].trim()
        }
      }
      
      // Fallback: return first 100 chars
      return content.substring(0, 100).trim() + (content.length > 100 ? '...' : '')
    }
    
    const submitFeedback = async (message, feedbackType) => {
      submittingFeedback.value = true
      try {
        const payload = {
          snapshot_id: message.snapshotId || 1, // Fallback ID for testing
          feedback_type: feedbackType
        }
        
        if (feedbackType === 'approval') {
          payload.decision = extractAnalysisFromMessage(message.content)
          payload.comment = 'User confirmed analysis is correct'
        }
        
        await api.post('/compliance-health/feedback/', payload)
        
        // Mark message as feedback given
        message.feedbackGiven = true
        
        // Refresh memories to show the new learning
        if (showMemoryPanel.value) {
          refreshMemories()
        }
        
      } catch (error) {
        console.error('Error submitting feedback:', error)
        messages.value.push({
          role: 'assistant',
          content: '⚠️ Could not record feedback. Please try again.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
      } finally {
        submittingFeedback.value = false
      }
    }
    
    const submitCorrection = async () => {
      submittingFeedback.value = true
      try {
        await api.post('/compliance-health/feedback/', {
          snapshot_id: correctionData.value.snapshotId || 1,
          feedback_type: 'correction',
          original_decision: correctionData.value.original,
          corrected_decision: correctionData.value.corrected,
          reason: correctionData.value.reason
        })
        
        // Mark original message as feedback given
        if (correctionData.value.messageIndex !== null) {
          messages.value[correctionData.value.messageIndex].feedbackGiven = true
        }
        
        // Add confirmation message
        messages.value.push({
          role: 'assistant',
          content: '✅ Thanks for the correction! I\'ve learned from your feedback and will apply this knowledge to future analyses.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
        
        // Refresh memories
        if (showMemoryPanel.value) {
          refreshMemories()
        }
        
        closeCorrectionModal()
        
      } catch (error) {
        console.error('Error submitting correction:', error)
        messages.value.push({
          role: 'assistant',
          content: '⚠️ Could not record correction. Please try again.',
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
      } finally {
        submittingFeedback.value = false
      }
    }
    
    // Auto-scroll when messages change
    watch(messages, scrollToBottom, { deep: true })
    
    return {
      isExpanded,
      messages,
      inputMessage,
      isTyping,
      unreadCount,
      messagesContainer,
      inputField,
      fileInput,
      remediationUploading,
      pendingRemediation,
      toggleChat,
      openChat,
      clearChat,
      formatMessage,
      formatTime,
      formatDocDate,
      openDocument,
      sendSuggestion,
      sendMessage,
      executeAction,
      viewFullList,
      triggerRemediationUpload,
      onRemediationFileSelected,
      // Memory & Feedback
      showMemoryPanel,
      memories,
      loadingMemories,
      showCorrectionModal,
      submittingFeedback,
      correctionData,
      toggleMemoryPanel,
      refreshMemories,
      getMemoryIcon,
      formatMemoryDate,
      openCorrectionModal,
      closeCorrectionModal,
      submitFeedback,
      submitCorrection
    }
  }
}
</script>

<style scoped>
.agent-chat {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.chat-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  transition: all 0.3s;
  font-weight: 600;
}

.chat-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(99, 102, 241, 0.5);
}

.chat-toggle i {
  font-size: 1.25rem;
}

.unread-badge {
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  margin-left: 0.25rem;
}

.chat-panel {
  width: 420px;
  height: 600px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.header-title i {
  font-size: 1.25rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.welcome-message {
  text-align: center;
  padding: 2rem 1rem;
}

.welcome-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #eff6ff, #f0fdf4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.welcome-icon i {
  font-size: 1.5rem;
  color: #6366f1;
}

.welcome-message h4 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.welcome-message p {
  color: #64748b;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.suggestion-btn {
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
}

.suggestion-btn:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.message {
  display: flex;
  gap: 0.75rem;
  max-width: 90%;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message.assistant {
  align-self: flex-start;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: #3b82f6;
  color: white;
}

.message.assistant .message-avatar {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.message-text {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 0.9rem;
}

.message.user .message-text {
  background: #3b82f6;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-text {
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 4px;
}

.message-time {
  font-size: 0.7rem;
  color: #94a3b8;
}

.message.user .message-time {
  text-align: right;
}

.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s;
}

.action-btn.notify {
  background: #fef3c7;
  color: #92400e;
}

.action-btn.notify:hover:not(:disabled) {
  background: #fde68a;
}

.action-btn.report {
  background: #dbeafe;
  color: #1e40af;
}

.action-btn.report:hover:not(:disabled) {
  background: #bfdbfe;
}

.action-btn.filter {
  background: #f0fdf4;
  color: #166534;
}

.action-btn.filter:hover:not(:disabled) {
  background: #dcfce7;
}

.action-btn.select {
  background: #ede9fe;
  color: #5b21b6;
}

.action-btn.select:hover:not(:disabled) {
  background: #ddd6fe;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.executed-badge {
  background: #10b981;
  color: white;
  font-size: 0.65rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  margin-left: 0.25rem;
}

.draft-hint {
  font-size: 0.65rem;
  opacity: 0.7;
  font-weight: normal;
  margin-left: 0.25rem;
}

.entity-preview {
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.85rem;
}

.entity-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #64748b;
}

.view-all-btn {
  background: none;
  border: none;
  color: #6366f1;
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
}

.preview-entity {
  display: flex;
  justify-content: space-between;
  padding: 0.375rem 0;
  border-bottom: 1px solid #e2e8f0;
}

/* Documents List */
.documents-list {
  margin-top: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.75rem;
}

.documents-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
}

.documents-header i {
  color: #6366f1;
}

.documents-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.document-card:hover {
  border-color: #6366f1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
  transform: translateY(-1px);
}

.doc-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  border-radius: 6px;
  color: #3b82f6;
  font-size: 1rem;
}

.doc-info {
  flex: 1;
  min-width: 0;
}

.doc-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: #64748b;
}

.doc-type {
  padding: 0.125rem 0.375rem;
  background: #e0e7ff;
  color: #4f46e5;
  border-radius: 4px;
  font-weight: 500;
}

.doc-date {
  display: flex;
  align-items: center;
}

.doc-action {
  flex-shrink: 0;
  color: #6366f1;
  font-size: 0.875rem;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.document-card:hover .doc-action {
  opacity: 1;
}

.preview-entity:last-child {
  border-bottom: none;
}

.entity-name {
  color: #1e293b;
  font-weight: 500;
}

.entity-issue {
  color: #ef4444;
  font-size: 0.75rem;
}

.more-entities {
  text-align: center;
  color: #94a3b8;
  padding-top: 0.5rem;
  font-size: 0.8rem;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 0.75rem 1rem;
  background: #f1f5f9;
  border-radius: 12px;
  width: fit-content;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}

.chat-input {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  align-items: flex-end;
}

.hidden-file-input {
  display: none;
}

.attach-btn {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
}

.attach-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.attach-btn:not(:disabled):hover {
  background: #e2e8f0;
}

.chat-input textarea {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  resize: none;
  font-family: inherit;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}

.chat-input textarea:focus {
  border-color: #6366f1;
}

.send-btn {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Sidebar Mode */
.agent-chat.sidebar {
  position: static;
  width: 100%;
  height: 100%;
}

.agent-chat.sidebar .chat-panel {
  width: 100%;
  height: 100%;
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.recipient-chip {
  display: inline-block;
  background: #f1f5f9;
  color: #475569;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
}

.target-list {
  margin-bottom: 1.5rem;
}

.target-list label {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.message-editor {
  font-family: inherit;
  line-height: 1.6;
  border: 2px solid #e2e8f0;
  padding: 1rem;
}

.message-editor:focus {
  border-color: #6366f1;
  outline: none;
}

.target-count i {
  color: #6366f1;
  margin-right: 0.5rem;
}

.agent-chat.sidebar .chat-toggle {
  display: none;
}

/* Notification Preview Modal */
.notification-preview {
  max-width: 700px;
  width: 90%;
}

.preview-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
}

.template-badge {
  background: #e0e7ff;
  color: #4338ca;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1e293b;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  border: none;
}

.btn-primary {
  background: #6366f1;
  color: white;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Responsive */
@media (max-width: 480px) {
  .chat-panel {
    width: calc(100vw - 2rem);
    height: calc(100vh - 4rem);
    position: fixed;
    bottom: 1rem;
    right: 1rem;
  }
  
  .toggle-label {
    display: none;
  }
}

/* =======================================================================
   MEMORY PANEL STYLES
   ======================================================================= */

.btn-icon.active {
  background: rgba(255, 255, 255, 0.4);
}

.memory-panel {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  max-height: 200px;
  overflow-y: auto;
}

.memory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
}

.memory-header h5 {
  margin: 0;
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.memory-header h5 i {
  color: #8b5cf6;
}

.btn-icon-sm {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
}

.btn-icon-sm:hover {
  background: #e2e8f0;
}

.memory-loading,
.memory-empty {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
}

.memory-empty i {
  font-size: 1.5rem;
  color: #cbd5e1;
  margin-bottom: 0.5rem;
  display: block;
}

.memory-empty p {
  margin: 0;
}

.memory-list {
  padding: 0.5rem;
}

.memory-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
}

.memory-item:last-child {
  margin-bottom: 0;
}

.memory-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.memory-item.procedural .memory-icon {
  background: #fef3c7;
  color: #92400e;
}

.memory-item.episodic .memory-icon {
  background: #dbeafe;
  color: #1e40af;
}

.memory-item.semantic .memory-icon {
  background: #dcfce7;
  color: #166534;
}

.memory-item.belief .memory-icon {
  background: #ede9fe;
  color: #5b21b6;
}

.memory-content {
  flex: 1;
  min-width: 0;
}

.memory-text {
  font-size: 0.8rem;
  color: #1e293b;
  line-height: 1.4;
  word-break: break-word;
}

.memory-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.7rem;
}

.memory-type {
  background: #f1f5f9;
  color: #64748b;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  text-transform: capitalize;
}

.memory-date {
  color: #94a3b8;
}

/* =======================================================================
   FEEDBACK BUTTON STYLES
   ======================================================================= */

.message-feedback {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.feedback-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.feedback-btn.approve {
  background: #f0fdf4;
  border-color: #86efac;
  color: #166534;
}

.feedback-btn.approve:hover {
  background: #dcfce7;
}

.feedback-btn.correct {
  background: #fefce8;
  border-color: #fde047;
  color: #854d0e;
}

.feedback-btn.correct:hover {
  background: #fef9c3;
}

.feedback-thanks {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #10b981;
}

.feedback-thanks i {
  color: #10b981;
}

/* =======================================================================
   CORRECTION MODAL STYLES
   ======================================================================= */

.correction-modal {
  max-width: 500px;
  width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
}

.modal-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.modal-body {
  padding: 1.25rem;
}

.correction-intro {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
}

.original-analysis {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.9rem;
  color: #92400e;
}

.modal-body .form-group {
  margin-bottom: 1rem;
}

.modal-body .form-group:last-child {
  margin-bottom: 0;
}

.modal-body label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1e293b;
  font-size: 0.85rem;
}

.modal-body .form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
}

.modal-body .form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}
</style>

